import { Alert } from 'components/ui/alert';
import { LoadingMessage } from 'components/Loader/LoadingMessage';
import { Dimensions, Direction } from 'eazychart-core';
import { ColumnChart } from 'eazychart-react';
import { ApiData } from 'interfaces/api';
import { AllChartConfigProps as AllChartProps, Chart } from 'interfaces/chart';
import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { guessDomainKey } from 'utils/chart';
import {
  DEFAULT_DIMENSIONS,
  GEO_CODE,
  INITIAL_CHART_CONFIG,
} from 'utils/constants';
import { enrichAxisTickFormat } from 'utils/axis';
import { useChart } from './useChart';
import { useData } from './useData';
import { usePageCarto } from './usePageCarto';

type ChartConfigContextType = {
  chart: ApiData<Chart>;
  ChartComponent: React.FC<AllChartProps>;
  updateChartProps: (_props: Partial<AllChartProps>) => void;
  updateChart: (config: Partial<Chart>) => void;
  dimensions: Dimensions;
  setDimensions: (_dimensions: Dimensions) => void;
  pageCartoId?: number;
};

const initialContext: ChartConfigContextType = {
  chart: INITIAL_CHART_CONFIG,
  ChartComponent: ColumnChart,
  updateChartProps: (_props: Partial<AllChartProps>) => undefined,
  updateChart: (_config: Partial<Chart>) => undefined,
  dimensions: DEFAULT_DIMENSIONS,
  setDimensions: (_dimensions: Dimensions) => undefined,
};

const ChartConfigContext =
  createContext<ChartConfigContextType>(initialContext);

type ChartConfigProviderProps = {
  chartId: number;
  children: ReactNode;
  pageCartoId?: number;
};

const normalizeChart = (
  chartData: ApiData<Chart> | undefined
): ApiData<Chart> => {
  const baseChart = chartData
    ? {
        ...chartData,
        indicators: [...(chartData.indicators ?? [])],
      }
    : {
        ...INITIAL_CHART_CONFIG,
        indicators: [...(INITIAL_CHART_CONFIG.indicators ?? [])],
      };

  const {
    props: { xAxis, yAxis, yLineAxis, ...restProps },
  } = baseChart;

  return {
    ...baseChart,
    props: {
      ...restProps,
      xAxis: xAxis ? enrichAxisTickFormat(xAxis) : xAxis,
      yAxis: yAxis ? enrichAxisTickFormat(yAxis) : yAxis,
      yLineAxis: yLineAxis ? enrichAxisTickFormat(yLineAxis) : yLineAxis,
    },
  };
};

export const ChartConfigProvider: FC<ChartConfigProviderProps> = ({
  chartId,
  children,
  pageCartoId,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { dataKeys, indicatorData, selectedGeoCode } = useData();
  const [dimensions, setDimensions] = useState<Dimensions>(DEFAULT_DIMENSIONS);
  const queryClient = useQueryClient();
  const { indicators } = usePageCarto();
  const {
    data: response,
    error,
    isLoading,
    ChartComponent,
  } = useChart(chartId);

  const rawChart = response?.data;
  // Global chart uses the raw data, while non global appears when a geo feature is selected
  const chart = useMemo(() => normalizeChart(rawChart), [rawChart]);

  const setChart = (newChart: ApiData<Chart> | undefined) => {
    const normalized = normalizeChart(newChart);
    queryClient.setQueryData(['chart', chartId], {
      data: {
        ...normalized,
        page_carto: pageCartoId ?? normalized.page_carto,
      },
    });
  };

  const updateChartProps = (props: Partial<AllChartProps>) => {
    setChart({
      ...chart,
      props: {
        ...chart.props,
        ...props,
      },
    });
  };

  const updateChart = (updates: Partial<Chart>) => {
    setChart({ ...chart, ...updates });
  };

  const chartIndicators = chart.indicators ?? [];

  const activeIndicators = useMemo(() => {
    if (!indicators || indicators.length === 0) {
      return [];
    }
    if (chartIndicators.length === 0) {
      return indicators;
    }
    const selectedSet = new Set(chartIndicators);
    const filtered = indicators.filter((indicator) =>
      selectedSet.has(indicator.id)
    );
    return filtered.length > 0 ? filtered : indicators;
  }, [chartIndicators, indicators]);

  const chartData = useMemo(() => {
    if (!indicatorData || indicatorData.length === 0) {
      return [];
    }

    const datum = selectedGeoCode
      ? indicatorData.find((d) => d[GEO_CODE] === selectedGeoCode) ||
        indicatorData[0]
      : indicatorData[0];

    if (!datum) {
      return [];
    }

    if (activeIndicators.length === 0) {
      return Object.entries(datum)
        .filter(([key]) => key !== GEO_CODE)
        .map(([key, value]) => ({ key, value }));
    }

    return activeIndicators
      .map(({ name }) => ({
        key: name,
        value: datum[name],
      }))
      .filter(({ value }) => value !== undefined);
  }, [activeIndicators, indicatorData, selectedGeoCode]);

  useEffect(() => {
    switch (chart.type) {
      case 'column':
        updateChartProps({
          xAxis: {
            ...chart.props.xAxis,
            domainKey: guessDomainKey(chartData, 'string', 0),
          },
          yAxis: {
            ...chart.props.yAxis,
            domainKey: guessDomainKey(chartData, 'number', 1),
          },
        });
        break;
      case 'lineColumn':
        updateChartProps({
          xAxis: {
            ...chart.props.xAxis,
            domainKey: guessDomainKey(chartData, 'string', 0),
          },
          yAxis: {
            ...chart.props.yAxis,
            domainKey: guessDomainKey(chartData, 'number', 1),
          },
        });
        break;
      case 'bar':
        updateChartProps({
          xAxis: {
            ...chart.props.xAxis,
            domainKey: guessDomainKey(chartData, 'number', 0),
          },
          yAxis: {
            ...chart.props.yAxis,
            domainKey: guessDomainKey(chartData, 'string', 1),
          },
        });
        break;
      case 'pie':
        updateChartProps({
          domainKey: guessDomainKey(chartData, 'number', 0),
          valueDomainKey: guessDomainKey(chartData, 'number', 0),
          labelDomainKey: guessDomainKey(chartData, 'string', 0),
        });
        break;
      case 'line':
        updateChartProps({
          xAxis: {
            ...chart.props.xAxis,
            domainKey: guessDomainKey(chartData, 'number', 0),
          },
          yAxis: {
            ...chart.props.yAxis,
            domainKey: guessDomainKey(chartData, 'number', 1),
          },
        });
        break;
      case 'area':
        updateChartProps({
          xAxis: {
            ...chart.props.xAxis,
            domainKey: guessDomainKey(chartData, 'number', 0),
          },
          yAxis: {
            ...chart.props.yAxis,
            domainKey: guessDomainKey(chartData, 'number', 1),
          },
        });
        break;
      case 'scatter':
        updateChartProps({
          xAxis: {
            ...chart.props.xAxis,
            domainKey: guessDomainKey(chartData, 'number', 0),
          },
          yAxis: {
            ...chart.props.yAxis,
            domainKey: guessDomainKey(chartData, 'number', 1),
          },
        });
        break;
      case 'bubble':
        updateChartProps({
          xAxis: {
            ...chart.props.xAxis,
            domainKey: guessDomainKey(chartData, 'number', 0),
          },
          yAxis: {
            ...chart.props.yAxis,
            domainKey: guessDomainKey(chartData, 'number', 1),
          },
        });
        break;

      default:
        break;
    }
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart.type, chartData, dataKeys]);

  if (isLoading || !isInitialized) {
    return <LoadingMessage />;
  }

  if (error) {
    return (
      <Alert variant="destructive">Impossible de charger le graphique</Alert>
    );
  }

  return (
    <ChartConfigContext.Provider
      value={{
        chart,
        updateChartProps,
        updateChart,
        ChartComponent,
        dimensions,
        setDimensions,
        pageCartoId,
      }}
    >
      {children}
    </ChartConfigContext.Provider>
  );
};

export const useChartConfig = () => {
  return useContext(ChartConfigContext);
};

export const useChartGridConfig = () => {
  const { chart, updateChartProps } = useChartConfig();

  const updateGridDirection = (direction: Direction, isEnabled: boolean) => {
    let gridDirections = [...chart.props.grid.directions] as Direction[];
    if (isEnabled) {
      gridDirections.push(direction);
    } else {
      gridDirections = gridDirections.filter((dir) => dir !== direction);
    }
    updateChartProps({
      grid: {
        ...chart.props.grid,
        directions: gridDirections,
      },
    });
  };

  const updateGridColor = (color: string) => {
    updateChartProps({
      grid: {
        ...chart.props.grid,
        color,
      },
    });
  };

  return {
    grid: chart.props.grid,
    updateGridDirection,
    updateGridColor,
  };
};
